import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { site } from "@/lib/site";
import { trackCall } from "@/lib/analytics";
import { breadcrumbJsonLd } from "@/lib/seo";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 pt-24">
      <div className="max-w-md text-center">
        <h1 className="font-display text-7xl font-semibold text-primary">404</h1>
        <h2 className="mt-4 text-xl font-semibold">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">The page you're looking for doesn't exist.</p>
        <Link to="/" className="btn-primary mt-6">Go home</Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
        <div className="mt-6 flex justify-center gap-3">
          <button onClick={() => { router.invalidate(); reset(); }} className="btn-primary">Try again</button>
          <a href="/" className="btn-outline">Home</a>
        </div>
      </div>
    </div>
  );
}

const ldJson = {
  "@context": "https://schema.org",
  "@type": "AccountingService",
  name: site.name,
  image: "https://www.ccobandcompany.com/og-image.png",
  logo: "https://www.ccobandcompany.com/og-image.png",
  telephone: site.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: site.address1,
    addressLocality: site.city,
    addressRegion: site.region,
    postalCode: site.postal,
    addressCountry: "US",
  },
  geo: { "@type": "GeoCoordinates", latitude: 28.5544, longitude: -81.5859 },
  url: "https://www.ccobandcompany.com",
  sameAs: [site.social.facebook, site.social.linkedin, site.social.nextdoor],
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"], opens: "08:00", closes: "18:00" },
  ],
  areaServed: "Winter Garden, FL and within 25 miles",
};

const OG_TITLE = "CCOB & Company PA — Trusted Winter Garden CPA Firm";
const OG_DESC = "Trusted CPA firm in Winter Garden, FL. 30+ years of accounting, auditing, tax preparation, payroll, and IRS representation.";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => {
    const abs = (p: string) => `https://www.ccobandcompany.com${p}`;
    return ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Winter Garden CPA Services | CCOB & Company PA" },
      { name: "description", content: "Trusted CPA firm in Winter Garden offering accounting, auditing, payroll, bookkeeping, tax preparation, IRS representation, and financial consulting with over 30 years of experience." },
      { name: "author", content: site.name },
      { property: "og:site_name", content: "CCOB & Company PA" },
      { property: "og:title", content: OG_TITLE },
      { property: "og:description", content: OG_DESC },
      { property: "og:image", content: abs("/og-image.png") },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "CCOB & Company PA — CPAs in Winter Garden, FL" },
      { name: "twitter:title", content: OG_TITLE },
      { name: "twitter:description", content: OG_DESC },
      { name: "twitter:image", content: abs("/og-image.png") },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg", media: "(prefers-color-scheme: light)" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon-white.svg", media: "(prefers-color-scheme: dark)" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon-180.png" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      { src: "https://www.googletagmanager.com/gtag/js?id=G-L37E537FS1", async: true },
      { children: "window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-L37E537FS1');" },
      { type: "application/ld+json", children: JSON.stringify(ldJson) },
    ],
    });
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head><HeadContent /></head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();
  const isStudio = router.state.location.pathname.startsWith("/admin");
  const breadcrumbs = breadcrumbJsonLd(router.state.location.pathname);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest('a[href^="tel:"]') as HTMLAnchorElement | null;
      if (!link) return;
      trackCall(link);
    };
    document.addEventListener("click", handler);
    return () => {
      document.removeEventListener("click", handler);
    };
  }, []);

  if (isStudio) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
      </QueryClientProvider>
    );
  }
  return (
    <QueryClientProvider client={queryClient}>
      <Header />
      {breadcrumbs && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbs) }} />}
      <main className="min-h-screen"><Outlet /></main>
      <Footer />
    </QueryClientProvider>
  );
}
